// DocumentPage — a Studio page for one document (pen "Pages", the lesson
// page; placed by pen Q76): its title, the facts line under it (its kind,
// the assistant's mark, its status: a planned page says Planned until
// something is written on it: by the author, on its paper, or from a
// source, never from a free description; the project's sources), what came
// of a generate, then the middle, which is
// the kind's own view (`middle`, rendered by the host: the editor, a source's
// original, a file's facts). The foot goes Back to the container it lies in
// and on to the Next part there.
//
// Pure: what is shown is projected in; every change goes out as an intent.

export { DocumentPage };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import type { DeskCommand, DeskDocument } from "../Studio/studioDesk.js";
import { NoteLine } from "../StudioDesk/DeskBriefs.js";
import { type DeskNote, docStateOf } from "../StudioDesk/deskFacts.js";
import { PageFacts, pageFactsOf } from "./PageFacts.js";
import { PageTitle } from "./PageTitle.js";
import type { PageCrumb, PageNodeKind } from "./studioPages.js";

const DocumentPage = ({
    doc,
    kind,
    suggested,
    sources,
    back,
    next,
    middle,
    note,
    onOpen,
    onSources,
    onRename,
}: {
    readonly doc: DeskDocument;
    readonly kind: PageNodeKind;
    readonly suggested: number;
    // The project's sources (undefined: not known yet).
    readonly sources: number | undefined;
    readonly back: PageCrumb | undefined;
    readonly next: PageCrumb | undefined;
    readonly middle: ReactNode;
    // What came of a generate (reading the sources shows in the middle).
    readonly note: DeskNote | undefined;
    readonly onOpen: (commands: readonly DeskCommand[]) => void;
    readonly onSources: () => void;
    // A new name for it (absent: not renamed here).
    readonly onRename: ((title: string) => void) | undefined;
}): ReactNode => {
    const { t } = useLingui();
    const state = docStateOf(doc);
    return (
        <article
            className="bilda-pages__page"
            aria-label={doc.title}
            data-kind={kind}
        >
            <PageTitle title={doc.title} onRename={onRename} />
            <PageFacts
                facts={pageFactsOf({
                    kind,
                    suggestion: doc.suggestion,
                    aiAuthored: doc.kind === "material" && doc.aiAuthored,
                    status:
                        suggested > 0
                            ? { type: "suggested", count: suggested }
                            : { type: "doc", state, ai: doc.aiAuthored },
                    sources,
                })}
                onSources={onSources}
            />
            {note === undefined || note.type === "reading" ? null : (
                <NoteLine note={note} />
            )}
            <div className="bilda-pages__page-middle">{middle}</div>
            {back === undefined && next === undefined ? null : (
                <footer className="bilda-pages__page-foot">
                    {back === undefined ? (
                        <span />
                    ) : (
                        <button
                            type="button"
                            className="bilda-pages__page-back"
                            onClick={() => onOpen(back.open)}
                        >
                            <Icon name="arrow-left" size="sm" />
                            {t`Back to ${back.title}`}
                        </button>
                    )}
                    {next === undefined ? null : (
                        <button
                            type="button"
                            className="bilda-pages__page-next"
                            onClick={() => onOpen(next.open)}
                        >
                            {t`Next: ${next.title}`}
                            <Icon name="arrow-right" size="sm" />
                        </button>
                    )}
                </footer>
            )}
        </article>
    );
};
